import React from "react";
import { usePlanFeatures } from "@/hooks/usePlanFeatures";
import FeaturePaywall from "@/components/common/FeaturePaywall";
import { Skeleton } from "@/components/ui/skeleton";
import type { FeatureSlug } from "@/lib/feature-gates";
import { featureSlugToName } from "@/lib/feature-gates";

interface FeatureGateProps {
  children: React.ReactNode;
  featureSlug: FeatureSlug;
  description?: string;
}

const FeatureGate: React.FC<FeatureGateProps> = ({
  children,
  featureSlug,
  description,
}) => {
  const { isEnabled, planName, isLoading } = usePlanFeatures();
  const featureName = featureSlugToName[featureSlug];

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="rounded-xl border border-border/60 p-4 space-y-3"
            >
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-3 w-16" />
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-border/60 p-4 space-y-3">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      </div>
    );
  }

  if (!isEnabled(featureSlug)) {
    return (
      <FeaturePaywall
        featureSlug={featureSlug}
        description={description}
        planName={planName}
        isLocked={true}
        isLoading={false}
      >
        <div className="p-6 space-y-6">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight">{featureName}</h1>
            <p className="text-muted-foreground">
              {description ||
                `Análise completa de ${featureName.toLowerCase()}`}
            </p>
          </div>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="rounded-xl border border-border/60 p-4 space-y-3"
              >
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-32" />
              </div>
            ))}
          </div>
        </div>
      </FeaturePaywall>
    );
  }

  return <>{children}</>;
};

export default FeatureGate;
